/**
 * TicketDashboard — aggregated metrics view embedded inside the
 * TicketsInboxPage when the user selects the "Dashboard" view toggle.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AlertCircle,
  Clock,
  CheckCircle2,
  Gauge,
  Layers,
  Tag,
  Activity,
  Calendar as CalendarIcon,
  TrendingUp,
  Timer,
  UserRound,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { API_BASE_URL as API } from '@/lib/apiBase';
import { useAuth } from '@/context/AuthContext';

const STATUS_DEFS = [
  { key: 'open', label: 'Open', color: 'bg-amber-50 text-amber-900 border-amber-200' },
  { key: 'in_progress', label: 'In Progress', color: 'bg-blue-50 text-blue-900 border-blue-200' },
  { key: 'waiting_on_customer', label: 'Waiting', color: 'bg-purple-50 text-purple-900 border-purple-200' },
  { key: 'under_investigation', label: 'Investigating', color: 'bg-cyan-50 text-cyan-900 border-cyan-200' },
  { key: 'resolved', label: 'Resolved', color: 'bg-emerald-50 text-emerald-900 border-emerald-200' },
];

function StatCard({ label, value, hint, icon: Icon, tone = 'slate', testId }) {
  const toneMap = {
    slate: 'text-slate-600 bg-slate-50',
    rose: 'text-rose-600 bg-rose-50',
    amber: 'text-amber-600 bg-amber-50',
    blue: 'text-blue-600 bg-blue-50',
    emerald: 'text-emerald-600 bg-emerald-50',
    indigo: 'text-indigo-600 bg-indigo-50',
    purple: 'text-purple-600 bg-purple-50',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="mt-1 text-3xl font-semibold text-slate-900">{value ?? '—'}</div>
          {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
        </div>
        {Icon && (
          <div className={`rounded-lg p-2 ${toneMap[tone] || toneMap.slate}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}

function DualStatCard({ label, perWeek, perMonth, icon: Icon, tone = 'indigo', testId }) {
  const toneMap = {
    indigo: 'text-indigo-600 bg-indigo-50',
    purple: 'text-purple-600 bg-purple-50',
    emerald: 'text-emerald-600 bg-emerald-50',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
          <div className="mt-2 grid grid-cols-2 gap-3">
            <div>
              <div className="text-2xl font-semibold text-slate-900">
                {perWeek ?? '—'}
              </div>
              <div className="text-[11px] text-slate-500">per week</div>
            </div>
            <div className="border-l border-slate-200 pl-3">
              <div className="text-2xl font-semibold text-slate-900">
                {perMonth ?? '—'}
              </div>
              <div className="text-[11px] text-slate-500">per month</div>
            </div>
          </div>
        </div>
        {Icon && (
          <div className={`rounded-lg p-2 ${toneMap[tone]}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}

function BarSeries({ data, label, testId }) {
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-500">
          {data.reduce((acc, d) => acc + d.count, 0)} tickets
        </div>
      </div>
      <div className="h-36 flex items-end gap-1.5">
        {data.map((d, i) => {
          const h = Math.round((d.count / max) * 100);
          return (
            <div key={`${d.label}-${i}`} className="flex-1 flex flex-col items-center gap-1 group">
              <div
                className="w-full bg-indigo-500 rounded-t hover:bg-indigo-600 transition-colors relative"
                style={{ height: `${h}%`, minHeight: d.count > 0 ? 4 : 0 }}
              >
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] text-slate-600 opacity-0 group-hover:opacity-100 whitespace-nowrap">
                  {d.count}
                </div>
              </div>
              <div className="text-[9px] text-slate-500 truncate w-full text-center">
                {d.label.replace(/^\d{4}-/, '')}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatHoursHuman(h) {
  if (h == null) return '—';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 24) return `${h.toFixed(1)}h`;
  const d = h / 24;
  return `${d.toFixed(1)}d`;
}

function buildMonthOptions(n = 12) {
  const options = [];
  const now = new Date();
  for (let i = 0; i < n; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-US', { month: 'short', year: 'numeric' });
    options.push({ key, label });
  }
  return options;
}

export function TicketDashboard({ locations = [] }) {
  const { isSuperAdmin } = useAuth();
  const showAssigneeWidget = typeof isSuperAdmin === 'function' ? isSuperAdmin() : false;
  const [selectedLocations, setSelectedLocations] = useState([]); // empty = all
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignees, setAssignees] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('all');
  const [timeMode, setTimeMode] = useState('all'); // 'all' | 'month' | 'custom'
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const monthOptions = useMemo(() => buildMonthOptions(12), []);

  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const { data: resp } = await axios.get(`${API}/tickets/assignees`);
        setAssignees(resp || []);
      } catch {
        // not staff? skip
      }
    };
    fetchAssignees();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        selectedLocations.forEach((id) => params.append('location_ids', id));
        if (timeMode === 'month' && selectedMonth) {
          params.append('month', selectedMonth);
        } else if (timeMode === 'custom') {
          if (dateFrom) params.append('start_date', `${dateFrom}T00:00:00.000Z`);
          if (dateTo) params.append('end_date', `${dateTo}T23:59:59.999Z`);
        }
        if (selectedAssignee !== 'all') {
          params.append('assignee_id', selectedAssignee);
        }
        const { data: resp } = await axios.get(
          `${API}/tickets/stats/dashboard?${params.toString()}`
        );
        setData(resp);
      } catch (e) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedLocations, selectedAssignee, timeMode, selectedMonth, dateFrom, dateTo]);

  const statusRows = useMemo(
    () =>
      STATUS_DEFS.map((s) => ({
        ...s,
        count: data?.status_counts?.[s.key] ?? 0,
      })),
    [data]
  );

  const locLabel = selectedLocations.length === 0
    ? 'All locations'
    : selectedLocations.length === 1
    ? locations.find((l) => l.id === selectedLocations[0])?.name || '1 location'
    : `${selectedLocations.length} locations`;

  const assigneeLabel = selectedAssignee === 'all'
    ? 'All assignees'
    : selectedAssignee === 'unassigned'
    ? 'Unassigned'
    : (assignees.find((a) => a.id === selectedAssignee)?.name
      || assignees.find((a) => a.id === selectedAssignee)?.email
      || 'Assignee');

  const timeLabel = timeMode === 'all'
    ? 'All time'
    : timeMode === 'month'
    ? (monthOptions.find((m) => m.key === selectedMonth)?.label || selectedMonth)
    : (dateFrom && dateTo ? `${dateFrom} → ${dateTo}` : 'Custom range');

  return (
    <div className="h-full overflow-auto p-4 space-y-4" data-testid="ticket-dashboard">
      {/* Filter bar */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-lg font-semibold text-slate-900">Overview</div>
          <div className="text-xs text-slate-500">
            {loading ? 'Loading…' : `${data?.total || 0} tickets · ${locLabel} · ${timeLabel} · ${assigneeLabel}`}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Time range control */}
          <Select value={timeMode} onValueChange={setTimeMode}>
            <SelectTrigger className="w-36 h-9" data-testid="dashboard-time-mode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All time</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="custom">Custom range</SelectItem>
            </SelectContent>
          </Select>

          {timeMode === 'month' && (
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-40 h-9" data-testid="dashboard-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((m) => (
                  <SelectItem key={m.key} value={m.key}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {timeMode === 'custom' && (
            <div className="flex items-center gap-2">
              <Input
                type="date"
                className="h-9 w-36"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="dashboard-date-from"
              />
              <span className="text-xs text-slate-400">to</span>
              <Input
                type="date"
                className="h-9 w-36"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="dashboard-date-to"
              />
            </div>
          )}

          {/* Assignee filter */}
          <Select value={selectedAssignee} onValueChange={setSelectedAssignee}>
            <SelectTrigger className="w-48 h-9" data-testid="dashboard-assignee-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All assignees</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
              {assignees.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.name || a.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Location filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-9" data-testid="dashboard-location-filter">
                <Layers className="w-4 h-4 mr-2" />
                {locLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="end">
              <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                <div className="text-xs font-medium text-slate-600">Locations</div>
                {selectedLocations.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedLocations([])}
                    className="text-[11px] text-indigo-600 hover:underline"
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="max-h-64 overflow-auto">
                {locations.map((loc) => {
                  const checked = selectedLocations.includes(loc.id);
                  return (
                    <label
                      key={loc.id}
                      className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          setSelectedLocations((prev) =>
                            v ? [...prev, loc.id] : prev.filter((id) => id !== loc.id)
                          );
                        }}
                        data-testid={`dashboard-loc-${loc.id}`}
                      />
                      <span className="text-sm text-slate-700 truncate">{loc.name}</span>
                    </label>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
        {statusRows.map((s) => (
          <div
            key={s.key}
            className={`rounded-xl border p-3 sm:p-4 ${s.color}`}
            data-testid={`dashboard-status-${s.key}`}
          >
            <div className="text-[10px] sm:text-[11px] uppercase tracking-wide font-medium opacity-80 truncate">{s.label}</div>
            <div className="mt-1 text-2xl sm:text-3xl font-semibold">{s.count}</div>
          </div>
        ))}
      </div>

      {/* SLA metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="SLA Breached"
          value={data?.sla?.breached ?? '—'}
          hint={`${data?.sla?.tracked ?? 0} tracked`}
          icon={AlertCircle}
          tone="rose"
          testId="dashboard-sla-breached"
        />
        <StatCard
          label="SLA Compliance"
          value={data?.sla?.compliance_pct != null ? `${data.sla.compliance_pct}%` : '—'}
          hint={`${data?.sla?.resolved_compliant ?? 0} / ${data?.sla?.resolved_total ?? 0} resolved in SLA`}
          icon={Gauge}
          tone="emerald"
          testId="dashboard-sla-compliance"
        />
        <StatCard
          label="Avg Response"
          value={formatHoursHuman(data?.sla?.avg_response_hours)}
          hint="From creation to first staff reply"
          icon={Clock}
          tone="blue"
          testId="dashboard-avg-response"
        />
        <StatCard
          label="Ticket Resolution Time"
          value={formatHoursHuman(data?.sla?.avg_resolution_hours)}
          hint="From creation to resolved status"
          icon={Timer}
          tone="purple"
          testId="dashboard-avg-resolution"
        />
      </div>

      {/* Average cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <DualStatCard
          label="Avg Ticket Completion"
          perWeek={data?.averages?.completion_per_week ?? '—'}
          perMonth={data?.averages?.completion_per_month ?? '—'}
          icon={CheckCircle2}
          tone="emerald"
          testId="dashboard-avg-completion"
        />
        <DualStatCard
          label="Avg Ticket Creation"
          perWeek={data?.averages?.creation_per_week ?? '—'}
          perMonth={data?.averages?.creation_per_month ?? '—'}
          icon={TrendingUp}
          tone="indigo"
          testId="dashboard-avg-creation"
        />
        <StatCard
          label="Total Resolved"
          value={data?.status_counts?.resolved ?? 0}
          hint={`Span: ${data?.averages?.span_days ?? 0} days`}
          icon={CheckCircle2}
          tone="emerald"
          testId="dashboard-total-resolved"
        />
      </div>

      {/* Tickets by assignee (placed right after averages row) — Super Admin only */}
      {showAssigneeWidget && (
      <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="dashboard-assignees">
        <div className="flex items-center gap-2 mb-3">
          <UserRound className="w-4 h-4 text-slate-500" />
          <div className="text-sm font-semibold text-slate-800">Tickets by assignee</div>
        </div>
        {(!data?.assignees || data.assignees.length === 0) ? (
          <div className="text-sm text-slate-400 italic">No assignee data</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                  <th className="text-left font-medium py-2 pr-3">Assignee</th>
                  <th className="text-center font-medium py-2 px-2">Total</th>
                  <th className="text-center font-medium py-2 px-2">Open</th>
                  <th className="text-center font-medium py-2 px-2">In&nbsp;Progress</th>
                  <th className="text-center font-medium py-2 px-2">Waiting</th>
                  <th className="text-center font-medium py-2 px-2">Resolved</th>
                  <th className="text-center font-medium py-2 pl-2">SLA Breach</th>
                </tr>
              </thead>
              <tbody>
                {data.assignees.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60 transition-colors"
                    data-testid={`dashboard-assignee-row-${a.id}`}
                  >
                    <td className="py-2 pr-3">
                      <div className="font-medium text-slate-800 truncate">
                        {a.name}
                      </div>
                      {a.email && a.id !== 'unassigned' && (
                        <div className="text-xs text-slate-500 truncate">{a.email}</div>
                      )}
                    </td>
                    <td className="text-center py-2 px-2">
                      <Badge variant="outline" className="bg-slate-50 text-slate-700">
                        {a.total}
                      </Badge>
                    </td>
                    <td className="text-center py-2 px-2 text-amber-700">{a.open || 0}</td>
                    <td className="text-center py-2 px-2 text-blue-700">{a.in_progress || 0}</td>
                    <td className="text-center py-2 px-2 text-purple-700">{a.waiting_on_customer || 0}</td>
                    <td className="text-center py-2 px-2 text-emerald-700">{a.resolved || 0}</td>
                    <td className="text-center py-2 pl-2">
                      {a.breached > 0 ? (
                        <span className="inline-flex items-center gap-1 text-rose-600 font-medium">
                          <AlertCircle className="w-3 h-3" /> {a.breached}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      {/* Trend charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <BarSeries data={data?.weekly || []} label="Last 12 weeks (created)" testId="dashboard-weekly" />
        <BarSeries data={data?.monthly || []} label="Last 12 months (created)" testId="dashboard-monthly" />
      </div>

      {/* Module + Ticket Type breakdowns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="dashboard-modules">
          <div className="flex items-center gap-2 mb-3">
            <Tag className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-semibold text-slate-800">Module breakdown</div>
          </div>
          {(!data?.modules || data.modules.length === 0) ? (
            <div className="text-sm text-slate-400 italic">No module data</div>
          ) : (
            <div className="space-y-2">
              {data.modules.slice(0, 10).map((m) => {
                const pct = data.total ? Math.round((m.count / data.total) * 100) : 0;
                return (
                  <div key={m.module}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-700 truncate">{m.module}</span>
                      <span className="text-slate-500 text-xs">
                        {m.count} · {pct}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="dashboard-ticket-types">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="w-4 h-4 text-slate-500" />
            <div className="text-sm font-semibold text-slate-800">Ticket types</div>
          </div>
          {(!data?.ticket_types || data.ticket_types.length === 0) ? (
            <div className="text-sm text-slate-400 italic">No ticket type data</div>
          ) : (
            <div className="space-y-2">
              {data.ticket_types.map((t) => {
                const pct = data.total ? Math.round((t.count / data.total) * 100) : 0;
                return (
                  <div key={t.key}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-700">{t.label}</span>
                      <span className="text-slate-500 text-xs">
                        {t.count} · {pct}%
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-purple-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
